export enum MaterialTab {
  Material = "material",
  Assembly = "assembly",
  Product = "product"
}

export type MaterialTabType = `${MaterialTab}`;

export const MaterialTabList: MaterialTabType[] = [
  MaterialTab.Material,
  MaterialTab.Assembly,
  MaterialTab.Product
];

export const MaterialTabIndexMap: Record<MaterialTabType, number> =
  Object.fromEntries(MaterialTabList.map((name, index) => [name, index])) as Record<
    MaterialTabType,
    number
  >;

/** SourceData Type values that belong to each MPA tab (includes plural forms from Veracity). */
export const MaterialTabTypeFilterValues: Record<MaterialTabType, string[]> = {
  [MaterialTab.Material]: ["material", "materials"],
  [MaterialTab.Product]: ["product", "products"],
  [MaterialTab.Assembly]: ["assembly", "assemblies"],
};

/** JSON keys that may hold the MPA type in Veracity sourceData. */
export const SourceMaterialTypeKeys = ["Type", "type", "ObjectType", "object_type", "objectType"] as const;

/** Reads MPA type from sourceData (supports PascalCase/camelCase and Type/ObjectType aliases). */
export function getSourceMaterialType(source: Record<string, unknown> | null | undefined): string | undefined {
  if (!source) {
    return undefined;
  }
  for (const key of SourceMaterialTypeKeys) {
    const raw = source[key];
    if (typeof raw === "string" && raw.trim()) {
      return raw.trim();
    }
  }
  return undefined;
}

/** Whether a sourceData row belongs on the given MPA tab. */
export function matchesMaterialTab(
  source: Record<string, unknown> | null | undefined,
  tabType: MaterialTabType,
): boolean {
  const rawType = getSourceMaterialType(source);
  if (!rawType) {
    return tabType === MaterialTab.Material;
  }
  return normalizeMaterialTabType(rawType) === tabType;
}

/** Normalizes Veracity/MPA type strings to tab filter values (material | assembly | product). */
export function normalizeMaterialTabType(type: string | null | undefined): MaterialTabType {
  if (!type?.trim()) {
    return MaterialTab.Material;
  }
  const normalized = type.trim().toLowerCase();
  switch (normalized) {
    case "materials":
      return MaterialTab.Material;
    case "products":
      return MaterialTab.Product;
    case "assemblies":
      return MaterialTab.Assembly;
    default:
      return normalized as MaterialTabType;
  }
}
